// src/services/CaseService.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  orderBy,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase";
import { Visit, Child, Contract, Parent, Case, ClientFile, Account } from "@visitproof/types";
import { clean } from "../utils/utils";
import {
  ServiceType,
  SERVICE_TYPE_PREFIXES,
  DEFAULT_CASE_PREFIX,
} from "@visitproof/types";

const caseCollectionRef = collection(db, "cases");
const visitCollectionRef = collection(db, "visits");
const parentsCollectionRef = collection(db, "parents");
const childrenCollectionRef = collection(db, "children");
const contractsCollectionRef = collection(db, "contracts");

/**
 * Get the prefix for a service type.
 * If multiple service types are provided, uses the first one.
 * Falls back to DEFAULT_CASE_PREFIX if no service type.
 */
function getServiceTypePrefix(serviceTypes?: ServiceType[]): string {
  if (!serviceTypes || serviceTypes.length === 0) {
    return DEFAULT_CASE_PREFIX;
  }
  // Use the first service type's prefix
  const primaryType = serviceTypes[0];
  return SERVICE_TYPE_PREFIXES[primaryType] || DEFAULT_CASE_PREFIX;
}

/**
 * Format a sequence number with leading zeros (3 digits).
 */
function formatSequence(sequence: number): string {
  return String(sequence).padStart(3, "0");
}

/**
 * Generate a case identifier in the format: {PREFIX}-{YEAR}-{SEQUENCE}
 * Example: SV-2026-001, EX-2026-015, CM-2026-003
 */
function generateCaseIdentifier(
  prefix: string,
  year: number,
  sequence: number
): string {
  return `${prefix}-${year}-${formatSequence(sequence)}`;
}

export const CaseService = {
  /**
   * Creates a case with an auto-generated identifier.
   * Uses a Firestore transaction to atomically increment the sequence counter.
   *
   * If an identifier is provided, it will be used as-is.
   * If no identifier is provided, one will be generated based on:
   * - Service type prefix (SV for supervised visitation, EX for safe exchange, etc.)
   * - Current year
   * - Sequential counter (per account, per year, per prefix)
   */
  async createCase(
    caseData: Omit<
      Case,
      "id" | "createdAt" | "updatedAt" | "users" | "children" | "status"
    >,
  ): Promise<string> {
    try {
      const { accountId, serviceTypes, identifier: providedIdentifier } = caseData;

      // If an identifier is provided, use it directly (skip auto-generation)
      if (providedIdentifier && providedIdentifier.trim()) {
        const newCase: Omit<Case, "id"> = {
          ...caseData,
          identifier: providedIdentifier.trim(),
          status: "open",
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };

        const caseRef = await addDoc(caseCollectionRef, clean(newCase));
        return caseRef.id;
      }

      // Auto-generate identifier using transaction
      const accountRef = doc(db, "accounts", accountId);
      const currentYear = new Date().getFullYear().toString();
      const prefix = getServiceTypePrefix(serviceTypes);

      const caseId = await runTransaction(db, async (transaction) => {
        // Get current account data
        const accountDoc = await transaction.get(accountRef);
        if (!accountDoc.exists()) {
          throw new Error("Account not found");
        }

        const accountData = accountDoc.data() as Account;
        const caseSequences = accountData.caseSequences || {};
        const yearSequences = caseSequences[currentYear] || {};
        const currentSequence = yearSequences[prefix] || 0;
        const newSequence = currentSequence + 1;

        // Generate the identifier
        const generatedIdentifier = generateCaseIdentifier(
          prefix,
          parseInt(currentYear),
          newSequence
        );

        // Update the sequence counter in the account
        const updatedSequences = {
          ...caseSequences,
          [currentYear]: {
            ...yearSequences,
            [prefix]: newSequence,
          },
        };

        transaction.update(accountRef, {
          caseSequences: updatedSequences,
        });

        // Create the case document
        const newCase: Omit<Case, "id"> = {
          ...caseData,
          identifier: generatedIdentifier,
          status: "open",
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };

        const newCaseRef = doc(caseCollectionRef);
        transaction.set(newCaseRef, clean(newCase));

        return newCaseRef.id;
      });

      return caseId;
    } catch (error) {
      console.error("Error creating case:", error);
      throw new Error(`Failed to create case: ${error}`);
    }
  },

  async getCase(caseId: string): Promise<Case | null> {
    try {
      const caseDoc = await getDoc(doc(caseCollectionRef, caseId));
      if (caseDoc.exists()) {
        return { id: caseDoc.id, ...caseDoc.data() } as Case;
      }
      return null;
    } catch (error) {
      console.error(`Error fetching case with ID ${caseId}:`, error);
      throw new Error(`Failed to get case: ${error}`);
    }
  },

  async getCaseVisits(caseId: string, accountId: string): Promise<Visit[]> {
    try {
      const visitSnapshot = await getDocs(
        query(
          visitCollectionRef,
          where("caseId", "==", caseId),
          where("accountId", "==", accountId),
          orderBy("startDate", "desc"),
        ),
      );
      return visitSnapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as Visit,
      );
    } catch (error) {
      console.error(`Error fetching visits for case ID ${caseId}:`, error);
      throw new Error(`Failed to get case visits: ${error}`);
    }
  },

  async getCaseParents(caseId: string, accountId: string): Promise<Parent[]> {
    try {
      const parentsSnapshot = await getDocs(
        query(
          parentsCollectionRef,
          where("caseId", "==", caseId),
          where("accountId", "==", accountId),
        ),
      );
      return parentsSnapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as Parent,
      );
    } catch (error) {
      console.error(`Error fetching parents for case ID ${caseId}:`, error);
      throw new Error(`Failed to get case parents: ${error}`);
    }
  },

  async getCaseChildren(caseId: string, accountId: string): Promise<Child[]> {
    try {
      const childrenSnapshot = await getDocs(
        query(
          childrenCollectionRef,
          where("caseId", "==", caseId),
          where("accountId", "==", accountId),
        ),
      );
      return childrenSnapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as Child,
      );
    } catch (error) {
      console.error(`Error fetching children for case ID ${caseId}:`, error);
      throw new Error(`Failed to get case children: ${error}`);
    }
  },

  async getCaseFiles(caseId: string): Promise<ClientFile[]> {
    try {
      const caseFilesSnapshot = await getDocs(
        collection(db, "cases", caseId, "files"),
      );
      if (caseFilesSnapshot.empty) {
        return [];
      }
      return caseFilesSnapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as ClientFile,
      );
    } catch (error) {
      return [];
    }
  },

  async getCaseContracts(
    caseId: string,
    accountId: string,
  ): Promise<Contract[]> {
    try {
      const contractsSnapshot = await getDocs(
        query(
          contractsCollectionRef,
          where("caseId", "==", caseId),
          where("accountId", "==", accountId),
        ),
      );
      if (contractsSnapshot.empty) {
        return [];
      }
      return contractsSnapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as Contract,
      );
    } catch (error) {
      console.error(`Error fetching contracts for case ID ${caseId}:`, error);
      throw new Error(`Failed to get case contracts: ${error}`);
    }
  },

  async addFileToCase(
    caseId: string,
    fileData: Omit<ClientFile, "id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    try {
      const newFile: Omit<ClientFile, "id"> = {
        ...fileData,
        caseId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      const fileRef = await addDoc(
        collection(db, "cases", caseId, "files"),
        newFile,
      );
      return fileRef.id;
    } catch (error) {
      console.error(`Error adding file to case with ID ${caseId}:`, error);
      throw new Error(`Failed to add file to case: ${error}`);
    }
  },

  async updateFileInCase(
    caseId: string,
    fileId: string,
    updatedData: Partial<Omit<File, "id" | "createdAt" | "updatedAt">>,
  ): Promise<void> {
    try {
      const fileRef = doc(collection(db, "cases", caseId, "files"), fileId);
      await updateDoc(fileRef, clean({
        ...updatedData,
        updatedAt: Timestamp.now(),
      }));
    } catch (error) {
      console.error(
        `Error updating file with ID ${fileId} in case ${caseId}:`,
        error,
      );
      throw new Error(`Failed to update file: ${error}`);
    }
  },

  async deleteFileFromCase(caseId: string, fileId: string): Promise<void> {
    try {
      const fileRef = doc(collection(db, "cases", caseId, "files"), fileId);
      await deleteDoc(fileRef);
    } catch (error) {
      console.error(
        `Error deleting file with ID ${fileId} from case ${caseId}:`,
        error,
      );
      throw new Error(`Failed to delete file: ${error}`);
    }
  },

  async getAllCases(accountId: string): Promise<Case[]> {
    try {
      const caseSnapshot = await getDocs(
        query(
          caseCollectionRef,
          where("accountId", "==", accountId),
          where("accountId", "==", accountId),
          orderBy("identifier", "asc"),
        ),
      );
      return caseSnapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as Case,
      );
    } catch (error) {
      console.error(`Error fetching cases for account ID ${accountId}:`, error);
      throw new Error(`Failed to get cases: ${error}`);
    }
  },

  async updateCase(
    caseId: string,
    updatedData: Partial<Omit<Case, "id" | "createdAt">>,
  ): Promise<void> {
    try {
      const caseDocRef = doc(caseCollectionRef, caseId);
      await updateDoc(caseDocRef, clean({
        ...updatedData,
        updatedAt: Timestamp.now(),
      }));
    } catch (error) {
      console.error(`Error updating case with ID ${caseId}:`, error);
      throw new Error(`Failed to update case: ${error}`);
    }
  },

  async deleteCase(caseId: string): Promise<void> {
    try {
      const caseDocRef = doc(caseCollectionRef, caseId);
      await deleteDoc(caseDocRef);
    } catch (error) {
      console.error(`Error deleting case with ID ${caseId}:`, error);
      throw new Error(`Failed to delete case: ${error}`);
    }
  },

  async removeParentFromCase(parentId: string): Promise<void> {
    try {
      const parentDocRef = doc(parentsCollectionRef, parentId);
      await deleteDoc(parentDocRef);
    } catch (error) {
      console.error(`Error removing parent with ID ${parentId}:`, error);
      throw new Error(`Failed to remove parent: ${error}`);
    }
  },
};
